/**
 * - `hash`: the link is only an anchor (`#faq`), so it stays on the page it is
 *   on. A path with an anchor (`/credit-card#faq`) is `internal`.
 * - `internal`: a path starting with a single `/`, or an http(s) or
 *   protocol-relative (`//host/x`) URL whose hostname contains `currentHost`.
 * - `external`: anything else — another host, another protocol (`mailto:`,
 *   `tel:`), a relative path without the leading `/`, a malformed URL.
 */
export type LinkType = 'internal' | 'external' | 'hash';

/**
 * `//host/x` names a host, not a path. The scheme is fixed to `https:`, so
 * read only the host and path of the result.
 */
export const resolveProtocolRelative = (link: string): string =>
  link.startsWith('//') ? `https:${link}` : link;

/**
 * @param link - The URL to classify
 * @param currentHost - The current hostname to compare against (e.g., 'www.moneyhero.com.hk')
 */
export const getLinkType = (link: string, currentHost: string): LinkType => {
  if (link.startsWith('#')) return 'hash';
  const absolute = resolveProtocolRelative(link);
  if (absolute.startsWith('/')) return 'internal';

  if (absolute.startsWith('http://') || absolute.startsWith('https://')) {
    // Parse absolute URL and compare hostname with current host
    try {
      const linkUrl = new URL(absolute);
      return linkUrl.hostname.includes(currentHost) ? 'internal' : 'external';
    } catch {
      // Invalid URL, treat as external
      return 'external';
    }
  }

  // Other protocols or malformed URLs are treated as external
  return 'external';
};

/**
 * Strict variant for decisions with SEO weight (e.g. `rel="sponsored"`): the
 * hostname must equal `currentHost` or be one of its subdomains. Unlike
 * `getLinkType`'s substring match, `www.singsaver.com.sg.tracker.example`
 * is NOT same-site. A path starting with `/` is same-site, a protocol-relative
 * link is judged by its host; anything else is not.
 */
export const isSameSiteLink = (link: string, currentHost: string): boolean => {
  const absolute = resolveProtocolRelative(link);
  if (absolute.startsWith('/')) return true;
  if (!absolute.startsWith('http://') && !absolute.startsWith('https://')) {
    return false;
  }

  try {
    const { hostname } = new URL(absolute);
    return hostname === currentHost || hostname.endsWith(`.${currentHost}`);
  } catch {
    return false;
  }
};
